import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useDomains, useCreateChecklistRun, useCreateChecklistTemplate } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ChecklistItemComposer, {
  newItemDraft,
  type ItemDraft,
} from '@/components/ChecklistItemComposer';
import SlugField, { type SlugStatus } from '@/components/SlugField';
import { currentYear } from '@houseops/shared';
import type { Season } from '@/lib/types';

/**
 * Full-page wizard for creating a checklist template. Mobile-first:
 * single column on small screens, 2-column metadata/items grid on lg.
 * Sticky bottom action bar holds the "start a run now" toggle and the
 * primary submit. Hidden until the gallery's CTA is wired up — until
 * then, only direct URL navigation reaches it.
 */
export default function ChecklistTemplateNew() {
  const navigate = useNavigate();
  const domains = useDomains();
  const create = useCreateChecklistTemplate();
  const startRun = useCreateChecklistRun();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const [domainId, setDomainId] = useState('');
  const [season, setSeason] = useState<Season>('any');
  const [recurrenceRule, setRecurrenceRule] = useState('');
  const [description, setDescription] = useState('');
  const [drafts, setDrafts] = useState<ItemDraft[]>(() => [newItemDraft()]);
  const [startNow, setStartNow] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filledItems = useMemo(
    () => drafts.filter((d) => d.label.trim() !== ''),
    [drafts],
  );

  const pristine =
    title.trim() === '' &&
    description.trim() === '' &&
    domainId === '' &&
    drafts.length === 1 &&
    drafts[0]!.label.trim() === '';

  // Warn before unload when there's unsaved typing.
  useEffect(() => {
    if (pristine) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [pristine]);

  const submitting = create.isPending || startRun.isPending;
  const submitDisabled =
    submitting ||
    !title.trim() ||
    !slug ||
    !domainId ||
    filledItems.length === 0 ||
    slugStatus === 'taken' ||
    slugStatus === 'checking';

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (filledItems.length === 0) {
      setError('Add at least one item.');
      return;
    }
    if (!domainId) {
      setError('Pick a domain.');
      return;
    }
    if (!slug) {
      setError('Slug required.');
      return;
    }

    try {
      const created = await create.mutateAsync({
        title: title.trim(),
        slug,
        domainId,
        description: description.trim() || null,
        season,
        recurrenceRule: recurrenceRule.trim() || null,
        items: filledItems.map((d) => ({
          label: d.label.trim(),
          description: d.description ?? null,
          required: d.required,
        })),
      });

      if (startNow) {
        const run = await startRun.mutateAsync({
          templateId: created.template.id,
          year: currentYear(),
          season: created.template.season ?? 'any',
        });
        navigate(`/checklists/runs/${run.run.id}`);
      } else {
        navigate(`/checklists/templates/${created.template.slug}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not save.';
      setError(message);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="pb-[calc(13rem+env(safe-area-inset-bottom))] sm:pb-[calc(11rem+env(safe-area-inset-bottom))] md:pb-[calc(7.5rem+env(safe-area-inset-bottom))] lg:pb-8"
    >
      <header className="space-y-2">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          <Link to="/checklists" className="hover:text-foreground">
            Checklists
          </Link>{' '}
          ·{' '}
          <Link to="/checklists/templates" className="hover:text-foreground">
            Templates
          </Link>{' '}
          · New
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
          New checklist template
        </h1>
        <p className="text-muted-foreground max-w-2xl">
          Capture a sequence of steps you want to repeat. Add the steps in the order you do
          them; mark the ones you can't skip as required. You can start a run from this
          template right after creating it.
        </p>
      </header>

      <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
        <section className="space-y-5" aria-labelledby="meta">
          <h2
            id="meta"
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground"
          >
            Metadata
          </h2>

          <div className="space-y-1.5">
            <Label
              htmlFor="title-input"
              className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
            >
              Title
            </Label>
            <Input
              id="title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Spring lawn startup"
              className="h-12 text-base"
              data-testid="wizard-title"
              required
            />
          </div>

          <SlugField
            source={title}
            value={slug}
            onChange={setSlug}
            onStatusChange={setSlugStatus}
          />

          <div className="space-y-1.5">
            <Label
              htmlFor="domain-select"
              className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
            >
              Domain
            </Label>
            <select
              id="domain-select"
              value={domainId}
              onChange={(e) => setDomainId(e.target.value)}
              className="h-12 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="wizard-domain"
              required
            >
              <option value="">Pick a domain…</option>
              {(domains.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label
                htmlFor="season-select"
                className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
              >
                Season
              </Label>
              <select
                id="season-select"
                value={season}
                onChange={(e) => setSeason(e.target.value as Season)}
                className="h-12 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="wizard-season"
              >
                <option value="any">any</option>
                <option value="spring">spring</option>
                <option value="summer">summer</option>
                <option value="fall">fall</option>
                <option value="winter">winter</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label
                htmlFor="recurrence-input"
                className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
              >
                Recurrence
              </Label>
              <Input
                id="recurrence-input"
                value={recurrenceRule}
                onChange={(e) => setRecurrenceRule(e.target.value)}
                placeholder="annual"
                className="h-12"
                data-testid="wizard-recurrence"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor="description-input"
              className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
            >
              Description
            </Label>
            <textarea
              id="description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Optional. What's the purpose? When do you run it?"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring resize-y"
              data-testid="wizard-description"
            />
          </div>
        </section>

        <section className="space-y-3" aria-labelledby="items">
          <h2
            id="items"
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground"
          >
            Items
          </h2>
          <p className="text-sm text-muted-foreground">
            Drag the handle to reorder. Tap <span className="font-mono">opt</span> to mark a
            step required.
          </p>
          <ChecklistItemComposer value={drafts} onChange={setDrafts} />
        </section>
      </div>

      {error ? (
        <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      ) : null}

      {/*
        Action bar. Three layout regimes:
          • below md: stacked above the BottomNav (which is fixed
            bottom-0 z-40). We sit on top of it with z-30 and a
            calc'd `bottom` that lifts past the nav + safe-area.
          • md to <lg: BottomNav is hidden, so we drop to bottom-0
            and absorb safe-area ourselves.
          • lg+: in flow at the end of the form, no fixed.
      */}
      <div
        className={
          'fixed inset-x-0 z-30 border-t border-rule bg-background/95 ' +
          'shadow-[0_-12px_24px_-12px_rgb(0_0_0_/_0.12)] backdrop-blur ' +
          'bottom-[calc(3.5rem+env(safe-area-inset-bottom))] ' +
          'md:bottom-0 md:pb-[env(safe-area-inset-bottom)] md:shadow-none ' +
          'lg:static lg:mt-8 lg:border-0 lg:bg-transparent lg:backdrop-blur-0 lg:pb-0 lg:shadow-none'
        }
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between lg:px-0">
          <label className="flex cursor-pointer select-none items-center gap-2.5 text-sm">
            <input
              type="checkbox"
              checked={startNow}
              onChange={(e) => setStartNow(e.target.checked)}
              className="h-4 w-4 cursor-pointer rounded border-input accent-primary"
              data-testid="wizard-start-now"
            />
            <span>Start a run from this template now</span>
          </label>

          <div className="flex items-center gap-2 sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/checklists/templates')}
              className="h-12 px-3 text-muted-foreground hover:text-foreground"
              data-testid="wizard-cancel"
            >
              <ChevronLeft className="mr-1 h-4 w-4" aria-hidden /> Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitDisabled}
              className="h-12 flex-1 sm:flex-initial sm:px-6"
              data-testid="wizard-submit"
            >
              {submitting
                ? 'Saving…'
                : startNow
                  ? 'Create and start'
                  : 'Create template'}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
